import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";

// Centered dialog on a dimmed backdrop. Closes on Esc and backdrop click,
// locks page scroll, and gives focus back to whatever opened it.
const Modal = ({ open, onClose, title, size = "md", children, labelledBy }) => {
  const panelRef = useRef(null);
  // Callers pass inline handlers; keep the latest without re-running setup.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    // Focus the first field, or the panel itself.
    requestAnimationFrame(() => {
      const target =
        panelRef.current?.querySelector("input, textarea, select") ||
        panelRef.current;
      target?.focus?.();
    });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const widths = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-2xl" };

  return createPortal(
    <div
      dir="rtl"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 font-sans"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || (title ? "modal-title" : undefined)}
        tabIndex={-1}
        className={`relative w-full ${widths[size] || widths.md} max-h-[92vh] overflow-y-auto bg-header text-white border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 focus:outline-none`}
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          {title ? (
            <h2 id="modal-title" className="text-xl font-extrabold">
              {title}
            </h2>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 -m-1 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
            aria-label="إغلاق"
          >
            <FaTimes />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
